import { useEffect, useState } from "react";
import { PortalLimitDefaults, User, api } from "../api";
import { useAuth } from "../auth";
import { UserRolesModal } from "../components/UserRolesModal";
import { NewUserModal } from "../components/NewUserModal";
import { SetPasswordModal } from "../components/SetPasswordModal";
import { DeleteUserModal } from "../components/DeleteUserModal";
import { RenameUserModal } from "../components/RenameUserModal";

const BREAK_GLASS_USERNAME = "admin";

function isProtected(u: User): boolean {
  return u.username === BREAK_GLASS_USERNAME;
}

function IconPencil() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 20h9M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconCheck() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden>
      <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconKey() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path
        d="M15.5 11.5a4 4 0 10-4 4M15.5 11.5L20 16M18 14l2 2M14 18l2 2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function IconLock() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="5" y="11" width="14" height="10" rx="2" />
      <path d="M8 11V8a4 4 0 018 0v3" strokeLinecap="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function AdminBadge() {
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      title="Administrator"
    >
      <span aria-hidden>★</span>adm
    </span>
  );
}

function ProtectedBadge() {
  return (
    <span
      className="inline-flex shrink-0 items-center gap-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide"
      title="Break-glass account; cannot be demoted, disabled, renamed, or deleted"
    >
      <span aria-hidden>🛡</span>bg
    </span>
  );
}

function HelpCallout({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] text-slate-600 leading-relaxed">
      <p className="font-semibold text-slate-800 mb-1.5">{title}</p>
      {children}
    </div>
  );
}

function PresenceCell({ u }: { u: User }) {
  const st = u.online_status;
  const raw = u.last_seen_at;
  const last =
    raw != null
      ? new Date(raw).toLocaleString()
      : st === "offline"
        ? "—"
        : "—";
  const badge =
    st === "online" ? (
      <span className="inline-flex rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 text-[10px] font-semibold">
        online
      </span>
    ) : st === "away" ? (
      <span className="inline-flex rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 text-[10px]">
        away
      </span>
    ) : st === "offline" ? (
      <span className="inline-flex rounded-full bg-slate-50 text-slate-500 border border-slate-200 px-1.5 py-0.5 text-[10px]">
        offline
      </span>
    ) : (
      <span className="text-[10px] text-slate-400">—</span>
    );
  return (
    <div className="text-[11px] text-slate-600 leading-tight">
      <div className="mb-0.5">{badge}</div>
      <div className="text-[10px] text-slate-400 max-w-[120px] truncate" title={last}>
        {last}
      </div>
    </div>
  );
}

function QuotaCell({
  token,
  userId,
  field,
  value,
  disabled,
  onSaved,
  inputId,
  title,
}: {
  token: string;
  userId: number;
  field: "rate_limit_per_minute" | "query_timeout_seconds";
  value: number | null | undefined;
  disabled: boolean;
  onSaved: () => void;
  inputId: string;
  title: string;
}) {
  const serverNum = value ?? 0;
  const [draft, setDraft] = useState(() => String(serverNum));
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDraft(String(value ?? 0));
  }, [userId, value]);

  const parsed = (() => {
    const t = draft.trim();
    if (t === "") return 0;
    const n = parseInt(t, 10);
    return Number.isFinite(n) ? n : NaN;
  })();
  const dirty = !Number.isFinite(parsed) || parsed !== serverNum;

  const commit = async () => {
    if (disabled || busy) return;
    if (!Number.isFinite(parsed) || parsed < 0) {
      alert("Enter a whole number ≥ 0 (0 = server default).");
      return;
    }
    const body =
      field === "rate_limit_per_minute"
        ? { rate_limit_per_minute: parsed === 0 ? null : parsed }
        : { query_timeout_seconds: parsed === 0 ? null : parsed };
    setBusy(true);
    try {
      await api.patchUser(token, userId, body);
      onSaved();
    } catch (e) {
      alert(`Save failed: ${(e as { message?: string })?.message ?? e}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="inline-flex items-center gap-0.5 max-w-full">
      <label className="sr-only" htmlFor={inputId}>
        {title}
      </label>
      <input
        id={inputId}
        className="border border-slate-200 rounded px-1 py-0.5 text-[11px] w-11 font-mono text-center"
        title={title}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        disabled={disabled || busy}
        inputMode="numeric"
      />
      {dirty && (
        <button
          type="button"
          className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
          title="Apply value"
          disabled={disabled || busy}
          onClick={() => void commit()}
        >
          <IconCheck />
        </button>
      )}
    </div>
  );
}

export function UsersPage() {
  const { token, me } = useAuth();
  const [users, setUsers] = useState<User[] | null>(null);
  const [limitDefaults, setLimitDefaults] = useState<PortalLimitDefaults | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [rolesUser, setRolesUser] = useState<User | null>(null);
  const [pwUser, setPwUser] = useState<User | null>(null);
  const [deleteUser, setDeleteUser] = useState<User | null>(null);
  const [renameUser, setRenameUser] = useState<User | null>(null);
  const [showNew, setShowNew] = useState(false);

  const reload = () => {
    if (!token) return;
    api
      .listUsers(token)
      .then(setUsers)
      .catch((e) => setError(String(e?.message ?? e)));
  };

  useEffect(reload, [token]);

  useEffect(() => {
    if (!token) {
      setLimitDefaults(null);
      return;
    }
    let cancelled = false;
    api
      .getPortalLimitDefaults(token)
      .then((d) => {
        if (!cancelled) setLimitDefaults(d);
      })
      .catch(() => {
        if (!cancelled) setLimitDefaults(null);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onToggleAdmin = async (u: User) => {
    if (!token) return;
    if (isProtected(u)) {
      alert(`Refusing to change admin status on the break-glass user '${u.username}'.`);
      return;
    }
    if (u.id === me?.id && u.is_admin) {
      alert("Refusing to demote yourself. Use another admin to do this.");
      return;
    }
    try {
      await api.patchUser(token, u.id, { is_admin: !u.is_admin });
      reload();
    } catch (e) {
      alert(`Update failed: ${(e as { message?: string })?.message ?? e}`);
    }
  };

  const onToggleDisabled = async (u: User) => {
    if (!token) return;
    if (isProtected(u)) {
      alert(`Refusing to disable the break-glass user '${u.username}'.`);
      return;
    }
    try {
      await api.patchUser(token, u.id, { disabled: !u.disabled });
      reload();
    } catch (e) {
      alert(`Update failed: ${(e as { message?: string })?.message ?? e}`);
    }
  };

  return (
    <div className="space-y-4">
      {token && rolesUser && (
        <UserRolesModal
          token={token}
          user={rolesUser}
          onClose={() => setRolesUser(null)}
          onSaved={() => reload()}
        />
      )}
      {token && showNew && (
        <NewUserModal
          token={token}
          onClose={() => setShowNew(false)}
          onCreated={() => reload()}
        />
      )}
      {token && pwUser && (
        <SetPasswordModal
          token={token}
          user={pwUser}
          onClose={() => setPwUser(null)}
        />
      )}
      {token && deleteUser && (
        <DeleteUserModal
          token={token}
          user={deleteUser}
          onClose={() => setDeleteUser(null)}
          onDeleted={() => reload()}
        />
      )}
      {token && renameUser && (
        <RenameUserModal
          token={token}
          user={renameUser}
          onClose={() => setRenameUser(null)}
          onSaved={() => reload()}
        />
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <h3 className="text-sm font-semibold text-slate-700">Users</h3>
        <button onClick={() => setShowNew(true)} className="btn-primary" type="button">
          + New user
        </button>
      </div>
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>
      )}
      <div className="card overflow-hidden">
        {users === null ? (
          <p className="p-4 text-sm text-slate-500">Loading…</p>
        ) : (
          <div className="w-full min-w-0">
            <table className="table-clean table-users w-full min-w-0 table-fixed">
              <colgroup>
                <col className="w-[4%]" />
                <col className="w-[30%]" />
                <col className="w-[8%]" />
                <col className="w-[8%]" />
                <col className="w-[9%]" />
                <col className="w-[9%]" />
                <col className="w-[14%]" />
                <col className="w-[6%]" />
                <col className="w-[12%]" />
              </colgroup>
              <thead>
                <tr>
                  <th className="text-right">#</th>
                  <th className="text-left">User</th>
                  <th className="text-center" title="Administrator vs analyst">
                    Role
                  </th>
                  <th className="text-center">Status</th>
                  <th
                    className="text-center"
                    title="Cap on MCP tool invocations per rolling 60s window (0 = server default)"
                  >
                    Tools/min
                  </th>
                  <th className="text-center" title="Max SELECT runtime in seconds (0 = server default)">
                    Timeout s
                  </th>
                  <th className="text-left">Presence</th>
                  <th className="text-right font-mono" title="MCP tool calls counted today">
                    Today
                  </th>
                  <th className="text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const protectedRow = isProtected(u);
                  const isSelf = u.id === me?.id;
                  return (
                    <tr key={u.id} className="border-t border-slate-100 align-middle">
                      <td className="font-mono text-right text-[11px] text-slate-500">{u.id}</td>
                      <td className="min-w-0">
                        <div className="flex items-center gap-1 min-w-0">
                          <span
                            className={`truncate min-w-0 ${u.is_admin ? "font-semibold text-slate-900" : "text-slate-800"}`}
                            title={u.username}
                          >
                            {u.username}
                          </span>
                          {protectedRow && <ProtectedBadge />}
                          <button
                            onClick={() => setRenameUser(u)}
                            className="btn shrink-0 h-7 w-7 p-0 inline-flex items-center justify-center"
                            type="button"
                            disabled={protectedRow}
                            title={protectedRow ? "Break-glass user cannot be renamed" : "Rename user"}
                          >
                            <span className="sr-only">Rename</span>
                            <IconPencil />
                          </button>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="inline-flex flex-col items-center gap-0.5">
                          {u.is_admin ? <AdminBadge /> : <span className="text-[10px] text-slate-500">analyst</span>}
                          <button
                            onClick={() => void onToggleAdmin(u)}
                            className="btn h-7 w-7 p-0 inline-flex items-center justify-center text-sm leading-none"
                            type="button"
                            disabled={protectedRow || (isSelf && u.is_admin)}
                            title={
                              protectedRow
                                ? "Break-glass user is always admin"
                                : isSelf && u.is_admin
                                  ? "Cannot demote yourself"
                                  : u.is_admin
                                    ? "Demote to analyst"
                                    : "Promote to admin"
                            }
                          >
                            <span className="sr-only">{u.is_admin ? "Demote to analyst" : "Promote to admin"}</span>
                            <span aria-hidden>{u.is_admin ? "↓" : "↑"}</span>
                          </button>
                        </div>
                      </td>
                      <td className="text-center">
                        <div className="inline-flex flex-col items-center gap-0.5">
                          {u.disabled ? (
                            <span
                              className="inline-flex rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide"
                              title="Account disabled"
                            >
                              off
                            </span>
                          ) : (
                            <span
                              className="inline-flex rounded-full bg-sky-50 text-sky-800 border border-sky-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide"
                              title="Active"
                            >
                              on
                            </span>
                          )}
                          <button
                            onClick={() => void onToggleDisabled(u)}
                            className="btn h-7 w-7 p-0 inline-flex items-center justify-center text-xs"
                            type="button"
                            disabled={protectedRow}
                            title={
                              protectedRow ? "Break-glass user cannot be disabled" : u.disabled ? "Re-enable account" : "Disable account"
                            }
                          >
                            <span className="sr-only">{u.disabled ? "Re-enable account" : "Disable account"}</span>
                            <span aria-hidden>{u.disabled ? "+" : "−"}</span>
                          </button>
                        </div>
                      </td>
                      <td className="text-center align-middle py-1.5">
                        {token ? (
                          <QuotaCell
                            token={token}
                            userId={u.id}
                            field="rate_limit_per_minute"
                            value={u.rate_limit_per_minute}
                            disabled={protectedRow}
                            onSaved={() => reload()}
                            inputId={`rl-${u.id}`}
                            title="MCP tool calls per minute cap (0 = server default)"
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="text-center align-middle py-1.5">
                        {token ? (
                          <QuotaCell
                            token={token}
                            userId={u.id}
                            field="query_timeout_seconds"
                            value={u.query_timeout_seconds}
                            disabled={protectedRow}
                            onSaved={() => reload()}
                            inputId={`qt-${u.id}`}
                            title="Query timeout in seconds (0 = server default)"
                          />
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="align-middle py-1.5">
                        <PresenceCell u={u} />
                      </td>
                      <td className="text-right font-mono text-[11px] align-middle py-1.5">
                        {u.mcp_invocations_today ?? "—"}
                      </td>
                      <td className="text-right align-middle py-1.5 pr-8">
                        <div className="inline-flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setRolesUser(u)}
                            className="btn-primary h-7 w-7 p-0 inline-flex items-center justify-center"
                            title="Assign roles"
                          >
                            <span className="sr-only">Assign roles</span>
                            <IconKey />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPwUser(u)}
                            className="btn h-7 w-7 p-0 inline-flex items-center justify-center"
                            title="Set password"
                          >
                            <span className="sr-only">Set password</span>
                            <IconLock />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteUser(u)}
                            className="btn-danger h-7 w-7 p-0 inline-flex items-center justify-center"
                            disabled={protectedRow || isSelf}
                            title={
                              protectedRow
                                ? "Break-glass user cannot be deleted"
                                : isSelf
                                  ? "Cannot delete yourself"
                                  : "Delete user"
                            }
                          >
                            <span className="sr-only">Delete user</span>
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <HelpCallout title="Limits">
        <ol className="list-decimal list-inside space-y-2 marker:font-semibold marker:text-slate-700">
          <li className="pl-0.5">
            <span>
              <strong className="text-slate-700">Tools/min:</strong> Caps how many MCP tool calls a user can start per
              rolling minute (abuse guard).
            </span>
            <div className="mt-1 ml-4 border-l-2 border-slate-200 pl-2 text-slate-600">
              <strong className="text-slate-700">Tools/min (Default)</strong> ={" "}
              {limitDefaults != null ? (
                <span className="font-mono tabular-nums">{limitDefaults.tools_per_minute_default}</span>
              ) : (
                <span className="text-slate-400">…</span>
              )}{" "}
              <span className="text-slate-500">
                (per minute, from server config — used when the cell shows <strong className="text-slate-700">0</strong>).
              </span>
            </div>
          </li>
          <li className="pl-0.5">
            <span>
              <strong className="text-slate-700">Timeout:</strong> The maximum time a single read query may run.
            </span>
            <div className="mt-1 ml-4 border-l-2 border-slate-200 pl-2 text-slate-600">
              <strong className="text-slate-700">Timeout (Default):</strong>{" "}
              {limitDefaults != null ? (
                <>
                  <span className="font-mono tabular-nums">{limitDefaults.query_timeout_seconds_default}</span> seconds
                </>
              ) : (
                <span className="text-slate-400">…</span>
              )}{" "}
              <span className="text-slate-500">
                (from server config — used when the cell shows <strong className="text-slate-700">0</strong>, meaning
                inherit default).
              </span>
            </div>
          </li>
        </ol>
        <p className="mt-2 pt-2 border-t border-slate-200 text-slate-600">
          Enter <strong className="text-slate-700">0</strong> in a cell to clear a custom value and use the default
          above. Edit a number, then click the <strong className="text-slate-700">green check</strong> to save that field
          only.
        </p>
      </HelpCallout>
      <HelpCallout title="Accounts & access rules">
        <p>
          MCP schema access is granted through <strong className="text-slate-700">roles</strong> (Roles tab): assign a
          user to roles with the <strong className="text-slate-700">key</strong> action; their access is the union of
          those roles' grants. Admin is conferred by the <strong className="text-slate-700">Administrator</strong> role.
          The break-glass user (<code className="text-slate-800">{BREAK_GLASS_USERNAME}</code>) cannot be demoted,
          disabled, renamed, or deleted — rotate its password instead. Demoting / disabling / deleting the last active
          admin is also refused server-side.
        </p>
      </HelpCallout>
    </div>
  );
}
